import Swiper from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";

export default class Outils {
  static init() {
    this.initSwiper();
    this.initCustomSelects();
    this.initSimulationFilter();
  }

  static initSwiper() {
    document.querySelectorAll(".outils").forEach((section) => {
      const sliderEl = section.querySelector(".outils__slider");
      if (!sliderEl) return;

      const prevBtn = section.querySelector(".outils__nav-btn--prev");
      const nextBtn = section.querySelector(".outils__nav-btn--next");

      // Keep a reference so AJAX-refreshed sections can update the carousel.
      section._swiper = new Swiper(sliderEl, {
        modules: [Navigation],
        slidesPerView: "auto",
        spaceBetween: 12,
        
        // --- DRAG & TOUCH EXPLICIT SETTINGS ---
        grabCursor: true,               // Shows the "grab" hand cursor on desktop
        simulateTouch: true,            // Allows dragging with the mouse
        allowTouchMove: true,           // Allows swiping on touch devices
        touchEventsTarget: 'container', // Listens for drag events on the whole container
        // --------------------------------------

        observer: true,
        observeParents: true,
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
        },
        breakpoints: {
          768: {
            spaceBetween: 16,
          },
          1024: {
            spaceBetween: 20,
          },
        },
      });
    });
  }

  static initCustomSelects() {
    // Initialize any custom-select component identified by the data attribute.
    // This initializer only owns selects rendered by the Outils component.
    // Other modules (for example ContactAgence and HeroSearch) initialize
    // their own custom selects and must not receive duplicate listeners.
    const selects = document.querySelectorAll('.outils__custom-select[data-custom-select]');

    selects.forEach(select => {
      const trigger = select.querySelector('[data-custom-select-trigger]');
      const valueDisplay = select.querySelector('[data-custom-select-value]');
      const hiddenInput = select.querySelector('[data-custom-select-input]');
      // Use role="option" to find option elements so different BEM namespaces work
      const options = select.querySelectorAll('[role="option"]');
      let isOpen = false;

      const openDropdown = () => {
        // Close any other open custom-selects globally
        document.querySelectorAll('[data-custom-select].is-open').forEach(other => {
          if (other === select) return;
          // Ask the other instance to close itself via a custom event
          other.dispatchEvent(new CustomEvent('closeCustomSelect'));
        });

        isOpen = true;
        select.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };

      const closeDropdown = () => {
        isOpen = false;
        select.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };

      const toggleDropdown = () => {
        isOpen ? closeDropdown() : openDropdown();
      };

      const selectOption = (option) => {
        // Update values
        valueDisplay.textContent = option.textContent.trim();
        hiddenInput.value = option.dataset.value;
        
        // Dispatch change event on hidden input in case other scripts listen to it
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));

        // Update ARIA states
        options.forEach(opt => opt.setAttribute('aria-selected', 'false'));
        option.setAttribute('aria-selected', 'true');

        closeDropdown();
        trigger.focus();
      };

      // Listen for a global close request so other instances can ask this one to close
      select.addEventListener('closeCustomSelect', () => {
        if (isOpen) closeDropdown();
      });

      // Mouse Events
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        toggleDropdown();
      });

      options.forEach((option, index) => {
        option.addEventListener('click', () => selectOption(option));

        // Keyboard Navigation within the listbox
        option.addEventListener('keydown', (e) => {
          switch(e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              selectOption(option);
              break;
            case 'ArrowDown':
              e.preventDefault();
              const nextOpt = options[index + 1] || options[0];
              nextOpt.focus();
              break;
            case 'ArrowUp':
              e.preventDefault();
              const prevOpt = options[index - 1] || options[options.length - 1];
              prevOpt.focus();
              break;
            case 'Escape':
              e.preventDefault();
              closeDropdown();
              trigger.focus();
              break;
          }
        });
      });

      // Keyboard Navigation on the trigger
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (!isOpen) openDropdown();
          
          // Focus the currently selected option, or the first one
          const selected = select.querySelector('[aria-selected="true"]') || options[0];
          selected.focus();
        } else if (e.key === 'Escape' && isOpen) {
          closeDropdown();
          trigger.focus();
        }
      });

      // Close when clicking outside
      document.addEventListener('click', (e) => {
        if (isOpen && !select.contains(e.target)) {
          closeDropdown();
        }
      });

      // Close when tabbing outside the component
      select.addEventListener('focusout', (e) => {
        if (isOpen && !select.contains(e.relatedTarget)) {
          closeDropdown();
        }
      });
    });
  }

  /**
   * AJAX filtering for the Simulation module: when the prestation filter
   * changes, fetch the matching cards and refresh the carousel.
   *
   * The second select lists the sub-services of every service at once; picking a
   * service reveals its own and hides the rest, so narrowing costs no round trip.
   */
  static initSimulationFilter() {
    document.querySelectorAll("[data-simulation]").forEach((section) => {
      const input = section.querySelector("[data-simulation-filter] [data-custom-select-input]");
      const wrapper = section.querySelector("[data-simulation-cards]");
      const ajaxUrl = section.dataset.ajaxUrl;
      const nonce = section.dataset.nonce;
      if (!input || !wrapper || !ajaxUrl) return;

      const subSelect = section.querySelector("[data-simulation-subfilter]");
      const subInput = subSelect && subSelect.querySelector("[data-custom-select-input]");
      const subTrigger = subSelect && subSelect.querySelector("[data-custom-select-trigger]");
      const subValue = subSelect && subSelect.querySelector("[data-custom-select-value]");
      const subOptions = subSelect ? Array.from(subSelect.querySelectorAll('[role="option"]')) : [];
      const subPlaceholder = subOptions.find((option) => !option.dataset.parent);

      // Reveal the sub-services of the chosen service, and keep the select disabled
      // as long as there is nothing to choose from.
      const syncSubFilter = () => {
        if (!subSelect || !subInput || !subTrigger || !subValue || !subPlaceholder) return;

        const parent = input.value;
        let available = 0;

        subOptions.forEach((option) => {
          if (!option.dataset.parent) return;

          const matches = parent !== "" && option.dataset.parent === parent;
          option.hidden = !matches;
          option.setAttribute("aria-selected", "false");
          if (matches) available += 1;
        });

        // Changing service invalidates whatever sub-service was picked before.
        subInput.value = "";
        subValue.textContent = subPlaceholder.textContent.trim();
        subPlaceholder.setAttribute("aria-selected", "true");

        const enabled = available > 0;
        subSelect.classList.toggle("is-disabled", !enabled);
        subTrigger.disabled = !enabled;
        subTrigger.setAttribute("aria-disabled", String(!enabled));

        if (!enabled) {
          subSelect.classList.remove("is-open");
          subTrigger.setAttribute("aria-expanded", "false");
        }
      };

      const refresh = async () => {
        const body = new URLSearchParams({
          action: "crc_simulation_filter",
          nonce: nonce || "",
          prestation: input.value || "",
          sub_prestation: (subInput && subInput.value) || "",
        });

        section.classList.add("is-loading");

        try {
          const res = await fetch(ajaxUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          const data = await res.json();
          if (data && data.success) {
            wrapper.innerHTML = data.data.html;
            if (section._swiper) {
              section._swiper.update();
              section._swiper.slideTo(0, 0);
            }
          }
        } catch (err) {
          console.error("Simulation filter failed:", err);
        } finally {
          section.classList.remove("is-loading");
        }
      };

      input.addEventListener("change", () => {
        syncSubFilter();
        refresh();
      });

      if (subInput) {
        subInput.addEventListener("change", refresh);
      }

      syncSubFilter();
    });
  }
}
